/**
 * Preferences Service - SDK-based implementation
 *
 * This service wraps the auto-generated SDK functions for user and organization preferences.
 * It provides a cleaner API with automatic org_id injection and consistent error handling.
 *
 * @module preferencesService
 */
import { getUserPreferences } from '@/sdk/api/user-preferences/user-preferences';
import { getOrgId } from '@/utils/organization/org_id_helper';
import logger from '@/utils/logger';

// Initialize SDK instance
const sdk = getUserPreferences();

/**
 * Response type for preference operations
 */
export interface PreferenceResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Preferences Service
 *
 * Provides methods for managing user and organization preferences using the SDK.
 */
export const preferencesService = {
  // ============================================
  // USER PREFERENCES (user-specific settings)
  // ============================================

  /**
   * Set a user-level preference
   *
   * @param key - Preference key (e.g., 'PREFERRED_LANGUAGE', 'THEME', 'MONITOR_PREFERENCE')
   * @param value - Preference value (will be converted to string)
   * @returns Promise resolving to success/failure
   */
  async setUserPreference(
    key: string,
    value: string | number | boolean,
  ): Promise<PreferenceResponse> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        logger.warn(
          '[PreferencesService] setUserPreference called without org_id',
        );
        return { success: false, error: 'No org_id available' };
      }

      logger.info(
        `[PreferencesService] 📤 setUserPreference called: key=${key.toUpperCase()}, value=${String(value)}, org_id=${org_id}`,
      );

      // Use async endpoint to avoid sync endpoint timeout issues (GitHub #8410)
      const response = await sdk.setUserPreferenceAsyncEndpointPreferencesSetUserPreferenceAsyncPut({
        org_id,
        key: key.toUpperCase(),
        value: String(value),
      });

      logger.info(
        `[PreferencesService] ✓ setUserPreference SUCCESS for key=${key.toUpperCase()}`,
        response,
      );

      return { success: true, data: response };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        `[PreferencesService] ❌ setUserPreference FAILED for key=${key}:`,
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to set user preference',
      };
    }
  },

  /**
   * Get a single user-level preference
   *
   * @param key - Preference key to retrieve
   * @returns Promise resolving to the preference value or null
   */
  async getUserPreference(key: string): Promise<string | null> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        logger.warn(
          '[PreferencesService] getUserPreference called without org_id',
        );
        return null;
      }

      const response = await sdk.getUserPreferencePreferencesGetUserPreferenceGet({
        org_id,
        key: key.toUpperCase(),
      });

      // SDK returns the value directly or null/undefined
      if (response === null || response === undefined) {
        return null;
      }

      // Handle different response formats
      if (typeof response === 'string') {
        return response;
      }
      if (typeof response === 'object' && response !== null && 'value' in response) {
        return (response as { value: string }).value;
      }

      return String(response);
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.warn(
        `[PreferencesService] getUserPreference failed for key=${key}:`,
        err?.message,
      );
      return null;
    }
  },

  /**
   * Get all user preferences
   *
   * @returns Promise resolving to object with all user preferences
   */
  async getAllUserPreferences(): Promise<Record<string, unknown>> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        logger.warn(
          '[PreferencesService] getAllUserPreferences called without org_id',
        );
        return {};
      }

      const response = await sdk.getUserPreferencesPreferencesGetUserPreferencesGet({
        org_id,
      });

      return (response as unknown as Record<string, unknown>) || {};
    } catch (error: unknown) {
      const err = error as { response?: { status?: number }; message?: string };
      // 401 errors are common during startup - return empty without throwing
      if (err?.response?.status === 401) {
        logger.warn(
          '[PreferencesService] getAllUserPreferences returned 401, returning empty preferences',
        );
        return {};
      }
      logger.error(
        '[PreferencesService] getAllUserPreferences failed:',
        error,
      );
      return {};
    }
  },

  /**
   * Remove a user preference
   *
   * @param key - Preference key to remove
   * @returns Promise resolving to success/failure
   */
  async removeUserPreference(key: string): Promise<PreferenceResponse> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return { success: false, error: 'No org_id available' };
      }

      await sdk.removeUserPreferencePreferencesRemoveUserPreferenceDelete({
        org_id,
        key: key.toUpperCase(),
      });

      return { success: true };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        `[PreferencesService] removeUserPreference failed for key=${key}:`,
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to remove user preference',
      };
    }
  },

  // ============================================
  // ORGANIZATION PREFERENCES (org-wide settings)
  // ============================================

  /**
   * Set a single organization-level preference
   *
   * @param key - Preference key
   * @param value - Preference value
   * @returns Promise resolving to success/failure
   */
  async setOrgPreference(
    key: string,
    value: string | number | boolean,
  ): Promise<PreferenceResponse> {
    // Secondary defense: reject undefined/null values to prevent backend error
    // "set_organisation_preference_async() missing 1 required positional argument: 'value'"
    if (value === undefined || value === null) {
      logger.error(
        `[PreferencesService] setOrgPreference called with undefined/null value for key=${key}`,
      );
      return {
        success: false,
        error: 'Value cannot be undefined or null',
      };
    }

    try {
      const org_id = getOrgId();
      if (!org_id) {
        return { success: false, error: 'No org_id available' };
      }

      const response = await sdk.setPreferencePreferencesSetPreferencePut({
        org_id,
        key: key.toUpperCase(),
        value: String(value),
      });

      return { success: true, data: response };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        `[PreferencesService] setOrgPreference failed for key=${key}:`,
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to set org preference',
      };
    }
  },

  /**
   * Get a single organization-level preference
   *
   * @param key - Preference key to retrieve
   * @returns Promise resolving to the preference value or null
   */
  async getOrgPreference(key: string): Promise<string | null> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return null;
      }

      const response = await sdk.getPreferencePreferencesGetPreferenceGet({
        org_id,
        key: key.toUpperCase(),
      });

      if (response === null || response === undefined) {
        return null;
      }

      if (typeof response === 'string') {
        return response;
      }
      if (typeof response === 'object' && response !== null && 'value' in response) {
        return (response as { value: string }).value;
      }

      return String(response);
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.warn(
        `[PreferencesService] getOrgPreference failed for key=${key}:`,
        err?.message,
      );
      return null;
    }
  },

  /**
   * Get all organization preferences
   *
   * @returns Promise resolving to object with all org preferences
   */
  async getAllOrgPreferences(): Promise<Record<string, unknown>> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return {};
      }

      const response = await sdk.getPreferencesPreferencesGetPreferencesGet({
        org_id,
      });

      return (response as Record<string, unknown>) || {};
    } catch (error: unknown) {
      logger.error(
        '[PreferencesService] getAllOrgPreferences failed:',
        error,
      );
      return {};
    }
  },

  /**
   * Set multiple organization preferences at once (bulk update)
   *
   * @param data - Object containing key-value pairs to set
   * @returns Promise resolving to success/failure
   */
  async setOrgPreferences(
    data: Record<string, string | number | boolean>,
  ): Promise<PreferenceResponse> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return { success: false, error: 'No org_id available' };
      }

      // Convert all values to strings for the API
      const stringData: Record<string, string> = {};
      for (const [key, value] of Object.entries(data)) {
        stringData[key] = String(value);
      }

      const response = await sdk.setPreferencesPreferencesSetPreferencesPut({
        org_id,
        data: JSON.stringify(stringData),
      });

      return { success: true, data: response };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        '[PreferencesService] setOrgPreferences failed:',
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to set org preferences',
      };
    }
  },

  /**
   * Remove an organization preference
   *
   * @param key - Preference key to remove
   * @returns Promise resolving to success/failure
   */
  async removeOrgPreference(key: string): Promise<PreferenceResponse> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return { success: false, error: 'No org_id available' };
      }

      await sdk.removePreferencePreferencesRemovePreferenceDelete({
        org_id,
        key: key.toUpperCase(),
      });

      return { success: true };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        `[PreferencesService] removeOrgPreference failed for key=${key}:`,
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to remove org preference',
      };
    }
  },

  // ============================================
  // SPECIALIZED PREFERENCES
  // ============================================

  /**
   * Set dashboard grouping preference
   * Note: document_type is not used by the SDK but kept for API compatibility
   */
  async setDashboardGroupingPreference(
    key: string,
    value: string,
    _document_type?: string, // Kept for compatibility, not used by SDK
  ): Promise<PreferenceResponse> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return { success: false, error: 'No org_id available' };
      }

      const response = await sdk.setGroupingDashboardPreferencePreferencesSetGroupingDashboardPreferencePut({
        org_id,
        key: key.toUpperCase(),
        value,
      });

      return { success: true, data: response };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        '[PreferencesService] setDashboardGroupingPreference failed:',
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to set dashboard grouping preference',
      };
    }
  },

  /**
   * Set purchase order grouping preference
   * Note: key is not used by the SDK but kept for API compatibility
   */
  async setPurchaseOrderGroupingPreference(
    _key: string, // Kept for compatibility, not used by SDK
    value: string,
  ): Promise<PreferenceResponse> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return { success: false, error: 'No org_id available' };
      }

      const response = await sdk.setGroupingPurchaseOrderPreferencePreferencesSetGroupingPurchaseOrderPreferencePut({
        org_id,
        value,
      });

      return { success: true, data: response };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        '[PreferencesService] setPurchaseOrderGroupingPreference failed:',
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to set PO grouping preference',
      };
    }
  },

  /**
   * Set reporting engine preference
   */
  async setReportingEnginePreference(
    key: string,
    value: string,
  ): Promise<PreferenceResponse> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return { success: false, error: 'No org_id available' };
      }

      const response = await sdk.setReportingEnginePreferencePreferencesSetReportingEnginePreferencePut({
        org_id,
        key: key.toUpperCase(),
        value,
      });

      return { success: true, data: response };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        '[PreferencesService] setReportingEnginePreference failed:',
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to set reporting engine preference',
      };
    }
  },

  /**
   * Get warehouse user preference
   * Note: This endpoint doesn't take a key parameter in the SDK
   */
  async getWarehouseUserPreference(_key?: string): Promise<string | null> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return null;
      }

      const response = await sdk.getWarehouseUserPreferencePreferencesGetWarehouseUserPreferenceGet({
        org_id,
      });

      if (typeof response === 'string') {
        return response;
      }
      if (typeof response === 'object' && response !== null && 'value' in response) {
        return (response as { value: string }).value;
      }

      return response ? String(response) : null;
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.warn(
        `[PreferencesService] getWarehouseUserPreference failed:`,
        err?.message,
      );
      return null;
    }
  },

  /**
   * Set cache preference
   */
  async setCachePreference(
    key: string,
    cacheName: string,
    enable: boolean,
    ttl?: number,
  ): Promise<PreferenceResponse> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return { success: false, error: 'No org_id available' };
      }

      const response = await sdk.setCachePreferencePreferencesSetCachePreferencePut({
        org_id,
        key: key.toUpperCase(),
        cache_name: cacheName,
        enable: String(enable),
        ttl,
      });

      return { success: true, data: response };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        '[PreferencesService] setCachePreference failed:',
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to set cache preference',
      };
    }
  },

  /**
   * Get cache preference
   */
  async getCachePreference(key: string): Promise<unknown> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return null;
      }

      const response = await sdk.getCachePreferencePreferencesGetCachePreferenceGet({
        org_id,
        key: key.toUpperCase(),
      });

      return response ?? null;
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.warn(
        `[PreferencesService] getCachePreference failed for key=${key}:`,
        err?.message,
      );
      return null;
    }
  },

  /**
   * Set TFIDF preference
   */
  async setTfidfPreference(
    key: string,
    tfidfId: string,
    value?: string,
    supplierId?: string,
  ): Promise<PreferenceResponse> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return { success: false, error: 'No org_id available' };
      }

      const response = await sdk.setTfidfPreferencePreferencesSetTfidfPreferencePut({
        org_id,
        key: key.toUpperCase(),
        tfidf_id: tfidfId,
        value,
        supplier_id: supplierId,
      });

      return { success: true, data: response };
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.error(
        '[PreferencesService] setTfidfPreference failed:',
        error,
      );
      return {
        success: false,
        error: err?.message || 'Failed to set TFIDF preference',
      };
    }
  },

  /**
   * Get TFIDF preference
   */
  async getTfidfPreference(key: string, tfidfId: string): Promise<unknown> {
    try {
      const org_id = getOrgId();
      if (!org_id) {
        return null;
      }

      const response = await sdk.getTfidfPreferenceSyncPreferencesGetTfidfPreferenceGet({
        org_id,
        key: key.toUpperCase(),
        tfidf_id: tfidfId,
      });

      return response ?? null;
    } catch (error: unknown) {
      const err = error as { message?: string };
      logger.warn(
        `[PreferencesService] getTfidfPreference failed:`,
        err?.message,
      );
      return null;
    }
  },
};

export default preferencesService;
